import type { PaginatedData } from '../@types';
import { keepPreviousData, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { ApiInstances, postData } from '../utils/api';

type Pagination = {
  page: number;
  limit: number;
};

export type PostQueryParams = {
  [key: string]: unknown;
};

type PaginatedPostQueryOptions<T> = Omit<UseQueryOptions<PaginatedData<T>>, 'queryKey' | 'queryFn'> & {
  queryKey?: UseQueryOptions<PaginatedData<T>>['queryKey'];
};

export const usePaginatedPostQuery = <T>(
  endPoint: string,
  bodyParams: PostQueryParams = {},
  apiService: keyof ApiInstances = 'casino',
  options?: PaginatedPostQueryOptions<T>
) => {
  const [paginationState, setPaginationState] = useState<Pagination>({
    page: 1,
    limit: 10,
  });

  const requestBody = useMemo(() => {
    return {
      ...bodyParams,
      limit: paginationState.limit,
      offset: (paginationState.page - 1) * paginationState.limit,
    };
  }, [bodyParams, paginationState]);
  const { queryKey: additionalQueryKey, ...queryOptions } = options ?? {};

  const { data, isFetching, isPending, refetch, status } = useQuery({
    queryKey: [endPoint, requestBody, additionalQueryKey],
    queryFn: (): Promise<PaginatedData<T>> => postData(endPoint, requestBody, apiService),
    ...queryOptions,
    placeholderData: keepPreviousData,
  });

  const setPage = useCallback((page: number) => {
    setPaginationState((prev) => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPaginationState((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  return {
    data,
    isFetching,
    isPending,
    isLoading: isPending,
    items: (data?.items as T[]) ?? [],
    totalCount: data?.count ?? 0,
    pageCount: data?.count ? Math.ceil(data.count / paginationState.limit) : 0,
    activePage: paginationState.page,
    limit: paginationState.limit,
    setPage,
    setLimit,
    refetch,
    status,
  };
};
